import { collection, getDocs, doc, getDoc, query, where, limit } from 'firebase/firestore';
import { db } from './firebase';
import { USER_TYPES } from '../constants/designer';
import { searchKey } from '../utils/searchKey';

// ── DESIGNER DIRECTORY ───────────────────────────────────────────────────────
// Reads designer accounts straight from users/{uid}, filtered on userType.
// There is no mirror collection: one document per user is the source of truth,
// so a profile edit is visible in search immediately and the two can never
// drift out of step.
//
// SEARCH IS CLIENT-SIDE by design. Firestore cannot match a substring — only a
// prefix — so a server query would find "Anna" from "Ann" but never from "nna",
// and every filter combination would need its own composite index. Fetching the
// directory once and filtering in memory gives substring matching on any field,
// instant results with no per-keystroke reads, and no index maintenance.
//
// The trade is that the whole directory is loaded up front, which is fine into
// the low thousands of designers. Past that this module is the single place to
// swap in a search service — nothing above it knows how matching works.
const MAX_DESIGNERS = 1000;
const CACHE_TTL_MS = 5 * 60 * 1000;

// users/{uid} → the flat card the search UI works with. The profile fields live
// nested under `designer`; flattening here means SearchPage and the profile page
// never have to know that, and the shape stays the same as before.
function toCard(id, u) {
  const d = u.designer || {};
  return {
    id,
    uid: id,
    fullName: u.fullName || d.fullName || '',
    avatarUrl: u.avatarUrl || '',
    // Maintained by the like trigger, never written from the client.
    likeCount: u.likeCount || 0,
    // ── WHICH TIER THIS DESIGNER IS ON ──
    // Carried so a card can show it (the tinted rail cards in
    // RecommendationsFeed) and so search can rank by it. This function is an
    // explicit whitelist rather than a spread, which is what keeps private
    // fields off a public card — and it is also why a field that is genuinely
    // needed has to be added here or it silently reads as undefined. That is
    // exactly what happened: `planOf` fell back to free for everyone, so a gold
    // account still drew the default card.
    //
    // Safe to expose: `plan` says which tier someone pays for, on a document
    // that is already world-readable for designers, and nothing about it is
    // private. `credits` is deliberately NOT here — a balance is nobody's
    // business but the account holder's.
    plan: u.plan || 'free',
    styles: d.styles || [],
    experienceYears: d.experienceYears ?? null,
    hourlyRate: d.hourlyRate ?? null,
    rateUnit: d.rateUnit ?? null,
    currency: d.currency || '',
    location: d.location || '',
    // The full picked line, e.g. "45, Khachatur Abovyan street, Arinj". The
    // `location` above stays the coarse "place, country" key that search
    // groups and filters on — see LocationPicker for why they are separate.
    // Falls back to it for profiles saved before addresses were kept.
    locationAddress: d.locationAddress || d.location || '',
    locationCoords: d.locationCoords || null,
    description: d.description || '',
    websites: d.websites || [],
    contacts: d.contacts || [],
  };
}

let _cache = null;
let _cachedAt = 0;
let _inflight = null;

/**
 * Applies a like/unlike to the CACHED copy of a designer, so a count you just
 * changed doesn't reappear stale.
 *
 * Without this: like a designer on their profile, press Back, and the feed
 * renders from a cache filled up to five minutes ago — showing the old number
 * and making the like look like it never happened. The alternative, dropping
 * the whole cache, would re-read every designer for a change of one integer on
 * one row; the value is already known here, so the row is simply corrected.
 *
 * The trigger remains the source of truth (functions/onLikeWrite.js). This only
 * keeps the local copy honest until the next real fetch.
 */
export function patchDesignerLikeCount(uid, delta) {
  if (!uid || !delta) return;
  // ONCE PER OBJECT, not once per cache.
  //
  // The per-uid cache holds its own copy when the designer was fetched
  // directly, but the SAME object when the directory already had them —
  // `fetchDesigner` stores the row it found rather than a copy (see below).
  // Patching both by name therefore applied the delta twice to one object,
  // and a single like moved the count by two: up two on like, down two on
  // unlike, for anyone who reached the profile from the feed or from search.
  //
  // Identity, not a uid check: it is the same object or it is not, and that is
  // exactly what must not be bumped twice.
  const seen = new Set();
  const bump = (row) => {
    if (!row || seen.has(row)) return;
    seen.add(row);
    row.likeCount = Math.max(0, (row.likeCount || 0) + delta);
  };
  bump(_cache?.find(d => (d.uid || d.id) === uid));
  bump(_one.get(uid)?.card);
}

/**
 * Forgets a designer, so the next read is fresh.
 *
 * MUST be called whenever a designer's own document changes. Without it, a
 * designer who edits their profile keeps seeing the pre-edit version for the
 * rest of the cache TTL — the one case where caching would be a visible
 * regression rather than a saving.
 */
export function invalidateDesigner(uid) {
  if (uid) _one.delete(uid);
  // The directory row is stale for the same reason.
  _cache = null;
  _cachedAt = 0;
}

/** Every designer account. One query, filtered on userType — no mirror
 *  collection to keep in step with the profile. */
export async function fetchDesigners({ force = false } = {}) {
  const fresh = _cache && Date.now() - _cachedAt < CACHE_TTL_MS;
  if (fresh && !force) return _cache;
  if (_inflight && !force) return _inflight;

  _inflight = (async () => {
    try {
      const snap = await getDocs(query(
        collection(db, 'users'),
        where('userType', '==', USER_TYPES.DESIGNER),
        limit(MAX_DESIGNERS),
      ));
      _cache = snap.docs
        .map(d => toCard(d.id, d.data()))
        // A profile with no name was never filled in; it would only ever show
        // as a blank row.
        .filter(d => d.fullName.trim());
      _cachedAt = Date.now();
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

// ── ONE DESIGNER ─────────────────────────────────────────────────────────────
// Memoised per uid, including the NEGATIVE result. Every OwnerBadge on a page
// calls this, so an ungated version cost one document read per tile: a grid of
// twenty liked photos was twenty reads, and paging a lightbox re-read the same
// author on every mount. Now it is one read per author per TTL, and a grid of
// twenty photos by one designer is a single read.
//
// Caching `null` matters as much as caching a hit — a customer's document is
// unreadable by the rules, and without this every render retried a request that
// is guaranteed to be refused.
const _one = new Map();       // uid -> { card, at }
const _oneInflight = new Map(); // uid -> Promise, so N badges mounting at once
                                // share ONE request instead of racing.

/** One designer, or null if that uid isn't a (readable) designer. */
export async function fetchDesigner(uid, { force = false } = {}) {
  if (!uid) return null;

  if (!force) {
    const hit = _one.get(uid);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.card;
    if (_oneInflight.has(uid)) return _oneInflight.get(uid);

    // The directory may already hold this designer — the feed and search fill
    // it — in which case there is nothing to fetch at all.
    const fromList = _cache?.find(d => (d.uid || d.id) === uid);
    if (fromList) {
      _one.set(uid, { card: fromList, at: Date.now() });
      return fromList;
    }
  }

  const req = (async () => {
    // A CUSTOMER's document is private, so reading one is denied by the rules —
    // that is correct behaviour, not a failure, and it must surface as "no
    // profile here" rather than an uncaught FirebaseError. Any read of a uid
    // that turns out not to be a designer lands in this catch.
    let snap;
    try {
      snap = await getDoc(doc(db, 'users', uid));
    } catch {
      return null;
    }
    if (!snap.exists()) return null;
    const u = snap.data();
    if (u.userType !== USER_TYPES.DESIGNER) return null;
    return toCard(snap.id, u);
  })();

  _oneInflight.set(uid, req);
  try {
    const card = await req;
    _one.set(uid, { card, at: Date.now() });
    return card;
  } finally {
    _oneInflight.delete(uid);
  }
}

// Normalises for comparison: lowercase, accents stripped. Without the accent
// fold, "Երևան" and "Erevan" aside, even "José" typed as "Jose" would miss.
// Lowercased, accents stripped, and TRANSLITERATED — so `ator` finds
// `աթոռ` and `стол` finds `stol`. Was a private copy of a
// lowercase-and-strip helper that each of the three directories kept its
// own identical version of; see utils/searchKey for what it does now and
// what it deliberately does not.
const norm = searchKey;

/**
 * Filters the directory.
 *
 * @param designers  rows from fetchDesigners()
 * @param q          free text — matches name, style labels, location, bio
 * @param filters    { styles: string[], minExperience: number|'', location: string }
 * @param styleLabel (key) => translated label, so a search for "modern" in any
 *                   of the three locales still hits the right style
 */
export function filterDesigners(designers, q, filters = {}, styleLabel = (k) => k) {
  const needle = norm(q);
  const { styles = [], minExperience = '', location = '' } = filters;
  const minExp = minExperience === '' || minExperience == null ? null : Number(minExperience);
  const loc = norm(location);

  return designers.filter(d => {
    // ── free text ──
    if (needle) {
      const haystack = [
        d.fullName,
        d.location,
        d.description,
        // Style KEYS and their translated labels, so "минимализм" and
        // "minimalist" both find the same designer.
        ...(d.styles || []),
        ...(d.styles || []).map(styleLabel),
      ].map(norm).join(' ');
      if (!haystack.includes(needle)) return false;
    }

    // ── styles: ALL selected must be present ──
    // "Modern AND Loft" is a narrowing filter; ANY-matching would make each
    // extra selection return more results, which is the opposite of what
    // ticking another box implies.
    if (styles.length && !styles.every(s => (d.styles || []).includes(s))) return false;

    // ── experience ──
    if (minExp != null && !Number.isNaN(minExp)) {
      if (d.experienceYears == null || Number(d.experienceYears) < minExp) return false;
    }

    // ── location ──
    if (loc && !norm(d.location).includes(loc)) return false;

    return true;
  });
}

/** Distinct locations present in the directory, for the filter dropdown. */
export function locationsOf(designers) {
  const seen = new Map();
  for (const d of designers) {
    const l = (d.location || '').trim();
    if (l && !seen.has(norm(l))) seen.set(norm(l), l);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}
